import "./GrossTotalDetails.scss";

import React, { useEffect, useMemo, useState } from "react";
import { formatDate, formatDateInEN } from "../../../commons/util/HallAppUtil";

import ReportAPI from "../ReportAPI";
import ReportFullFilter from "../../report-full/ReportFullFilter";
import ReportHeader from "../../report-full/components/ReportHeader";
import Skeleton from "../../../commons/skeleton/Skeleton";
import { useNavigate } from "react-router-dom";

const entryTypes = [
    { key: "ALL", label: "Todas" },
    { key: "COMMAND", label: "Comandas" },
    { key: "EXTERNAL", label: "Entradas externas" },
];

function formatBRL(value) {
    const n = Number(value || 0);
    return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatDateTime(iso) {
    if (!iso) return "-";
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function formatOnlyDate(iso) {
    if (!iso) return "-";
    const d = new Date(iso);
    return d.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
}

const reportAPI = new ReportAPI();

/**
 * Back retorna:
 * [{ sourceId, amount, source: "COMMAND"|"EXTERNAL", description, eventDate: "YYYY-MM-DD", createdAt: "YYYY-MM-DDTHH:mm:ss" }]
 *
 * Cursor pagination (keyset):
 * - primeira página: cursor_* = null
 * - próxima página: cursor_* = valores do ÚLTIMO item retornado (o mais “antigo” da página)
 */
export default function GrossTotalDetails() {
    const [activeType, setActiveType] = useState("ALL");
    const [search, setSearch] = useState("");
    const [summary, setSummary] = useState({});

    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    const navigate = useNavigate();

    const [filter, setFilter] = useState({
        companyId: 1, // ajuste conforme seu contexto
        limit: 5,
        startDate: formatDateInEN(firstDayOfMonth),
        endDate: formatDateInEN(lastDayOfMonth),

        // cursores
        cursorEventDate: null,
        cursorCreatedAt: null,
        cursorSource: null,
        cursorSourceId: null,
    });

    // dados lista
    const [rows, setRows] = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    // dados summary
    const [loadingSummary, setLoadingSummary] = useState(false);

    useEffect(() => {
        fetchFirstPage();
        fetchSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchSummary = async (reqFilter = filter) => {
        setLoadingSummary(true);
        try {
            const response = await reportAPI.getReportTotalGrossSummary(reqFilter);
            setSummary(response?.data || {});
        } catch (e) {
            setSummary({});
        } finally {
            setLoadingSummary(false);
        }
    };

    const fetchFirstPage = async (reqFilter = filter) => {
        if (loadingList) return;

        setLoadingList(true);
        setHasMore(true);

        const req = {
            ...reqFilter,
            cursorEventDate: null,
            cursorCreatedAt: null,
            cursorSource: null,
            cursorSourceId: null,
        };

        try {
            const response = await reportAPI.getReportTotalGross(req);
            const list = Array.isArray(response?.data) ? response.data : [];

            setRows(list);

            if (list.length < req.limit) setHasMore(false);

            const last = list[list.length - 1];
            setFilter((prev) => ({
                ...prev,
                ...req, // garante start/end/company/limit atualizados
                cursorEventDate: last ? last.eventDate : null,
                cursorCreatedAt: last ? last.createdAt : null,
                cursorSource: last ? last.source : null,
                cursorSourceId: last ? last.sourceId : null,
            }));
        } catch (e) {
            setRows([]);
            setHasMore(false);
        } finally {
            setLoadingList(false);
        }
    };

    const fetchNextPage = async () => {
        if (!hasMore || loadingMore) return;
        setLoadingMore(true);

        const req = { ...filter };

        try {
            const response = await reportAPI.getReportTotalGross(req);
            const list = Array.isArray(response?.data) ? response.data : [];

            setRows((prev) => [...prev, ...list]);

            if (list.length < req.limit) setHasMore(false);

            const last = list[list.length - 1];
            setFilter((prev) => ({
                ...prev,
                cursorEventDate: last ? last.eventDate : prev.cursorEventDate,
                cursorCreatedAt: last ? last.createdAt : prev.cursorCreatedAt,
                cursorSource: last ? last.source : prev.cursorSource,
                cursorSourceId: last ? last.sourceId : prev.cursorSourceId,
            }));
        } catch (e) {
            setHasMore(false);
        } finally {
            setLoadingMore(false);
        }
    };

    // filtro no front (tipo + busca) em cima do que já foi carregado
    const filtered = useMemo(() => {
        const byType = activeType === "ALL" ? rows : rows.filter((i) => i.source === activeType);

        const q = search.trim().toLowerCase();
        if (!q) return byType;

        return byType.filter((i) => {
            const hay = [
                i.source,
                i.sourceId,
                i.description,
                i.eventDate,
                i.createdAt,
                i.amount,
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

            return hay.includes(q);
        });
    }, [rows, activeType, search]);

    // agrupar por data
    const grouped = useMemo(() => {
        const map = new Map();

        const sorted = [...filtered].sort((a, b) => {
            const ad = a.eventDate || "";
            const bd = b.eventDate || "";
            if (ad !== bd) return bd.localeCompare(ad);

            const ac = a.createdAt || "";
            const bc = b.createdAt || "";
            if (ac !== bc) return bc.localeCompare(ac);

            const as = a.source || "";
            const bs = b.source || "";
            if (as !== bs) return bs.localeCompare(as);

            return Number(b.sourceId || 0) - Number(a.sourceId || 0);
        });

        for (const item of sorted) {
            const key = item.eventDate ? formatOnlyDate(item.eventDate) : "Sem data";
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(item);
        }

        return Array.from(map.entries()).map(([date, items]) => ({ date, items }));
    }, [filtered]);

    function changeDates(startDate, endDate) {
        // importante: não “perder” companyId/limit/cursors etc
        const next = {
            ...filter,
            startDate,
            endDate,
            cursorEventDate: null,
            cursorCreatedAt: null,
            cursorSource: null,
            cursorSourceId: null,
        };

        setFilter(next);
        setRows([]);

        fetchFirstPage(next);
        fetchSummary(next);
    }

    const showListSkeleton = loadingList;
    const showEmpty = !loadingList && grouped.length === 0;

    return (
        <div className="report-gross-full">
            <ReportHeader name="Total Bruto" />

            <div className="he-subHeader">
                <div className="he-subHeaderInner">
                    <ReportFullFilter changeDates={changeDates} />
                </div>
            </div>

            <div className="he-page" style={{ marginTop: "60px" }}>
                <main className="he-main">
                    <section className="he-summary">
                        <div className="he-summaryCard">
                            <div className="he-summaryTop">
                                <div className="he-chip">Período</div>
                                <div className="he-period">
                                    {formatDate(filter.startDate)} → {formatDate(filter.endDate)}
                                </div>
                            </div>

                            <div className="he-totalValue">
                                {loadingSummary ? <Skeleton height={28} width={180} variant="text" /> : formatBRL(summary?.totalGross)}
                            </div>

                            <div className="he-totalHint">
                                Soma de comandas + entradas externas (e futuros tipos)
                            </div>

                            <div className="he-breakdown">
                                <div className="he-breakItem">
                                    <div className="he-breakLabel">Comandas</div>
                                    <div className="he-breakValue">
                                        {loadingSummary ? (
                                            <Skeleton height={16} width={120} variant="text" />
                                        ) : (
                                            formatBRL(summary?.totalCommand)
                                        )}
                                    </div>
                                </div>

                                <div className="he-breakItem">
                                    <div className="he-breakLabel">Entradas externas</div>
                                    <div className="he-breakValue">
                                        {loadingSummary ? (
                                            <Skeleton height={16} width={120} variant="text" />
                                        ) : (
                                            formatBRL(summary?.totalExternal)
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="he-controls">
                            <div className="he-tabs">
                                {entryTypes.map((t) => (
                                    <button
                                        key={t.key}
                                        type="button"
                                        className={`he-tab ${activeType === t.key ? "is-active" : ""}`}
                                        onClick={() => setActiveType(t.key)}
                                        disabled={loadingList}
                                    >
                                        {t.label}
                                    </button>
                                ))}
                            </div>

                            <div className="he-search">
                                <span className="he-searchIcon">⌕</span>
                                <input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Buscar por descrição, id, data..."
                                    disabled={loadingList}
                                />
                                {search ? (
                                    <button
                                        className="he-clear"
                                        onClick={() => setSearch("")}
                                        aria-label="Limpar"
                                        type="button"
                                        disabled={loadingList}
                                    >
                                        ✕
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    </section>

                    <section className="he-list">
                        {showListSkeleton ? (
                            <div className="he-skeletonList">
                                {/* “dias” fake */}
                                {[1, 2].map((k) => (
                                    <div className="he-dayGroup" key={k}>
                                        <div className="he-dayHeader">
                                            <Skeleton width={140} height={14} variant="text" />
                                            <Skeleton width={70} height={14} variant="text" />
                                        </div>

                                        <div className="he-items">
                                            {Array.from({ length: 3 }).map((_, idx) => (
                                                <div className="he-item he-item--skeleton" key={idx}>
                                                    <Skeleton width={40} height={40} variant="circle" className="he-itemSkIcon" />

                                                    <div className="he-itemBody">
                                                        <div className="he-itemTop">
                                                            <Skeleton width={"55%"} height={14} variant="text" />
                                                            <Skeleton width={90} height={14} variant="text" />
                                                        </div>
                                                        <div className="he-itemSub">
                                                            <Skeleton width={"70%"} height={12} variant="text" />
                                                        </div>
                                                        <div className="he-itemMeta">
                                                            <Skeleton width={160} height={12} variant="text" />
                                                            <Skeleton width={80} height={12} variant="text" />
                                                        </div>
                                                    </div>

                                                    <div className="he-chevron">
                                                        <Skeleton width={10} height={18} variant="text" />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : showEmpty ? (
                            <div className="he-empty">
                                <div className="he-emptyTitle">Nada encontrado</div>
                                <div className="he-emptyText">Tente mudar o filtro ou ajustar a busca.</div>
                            </div>
                        ) : (
                            grouped.map((g) => (
                                <div className="he-dayGroup" key={g.date}>
                                    <div className="he-dayHeader">
                                        <span>{g.date}</span>
                                        <span className="he-dayCount">{g.items.length} itens</span>
                                    </div>

                                    <div className="he-items">
                                        {g.items.map((item) => {
                                            const isCommand = item.source === "COMMAND";

                                            return (
                                                <button
                                                    key={`${item.source}-${item.sourceId}`}
                                                    className={`he-item ${isCommand ? "is-command" : "is-external"}`}
                                                    type="button"
                                                    onClick={() => {
                                                        if (isCommand) {
                                                            navigate(`/relatorio?id=${item.sourceId}`)
                                                        } else {
                                                            navigate(`/external-transactions?id=${item.sourceId}`)
                                                        }
                                                    }}
                                                >
                                                    <div className="he-itemLeft">
                                                        <div className="he-icon" aria-hidden="true">
                                                            {isCommand ? (
                                                                // Ícone genérico de "comanda/serviço" (clipboard/list)
                                                                <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
                                                                    <path
                                                                        d="M9 6h6M9 10h6M9 14h4M7 4h10a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z"
                                                                        stroke="currentColor"
                                                                        strokeWidth="1.8"
                                                                        strokeLinecap="round"
                                                                    />
                                                                </svg>
                                                            ) : (
                                                                // Ícone genérico de "entrada externa" (seta entrando)
                                                                <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
                                                                    <path
                                                                        d="M12 3v10m0 0 3-3m-3 3-3-3M5 21h14"
                                                                        stroke="currentColor"
                                                                        strokeWidth="1.8"
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                    />
                                                                </svg>
                                                            )}
                                                        </div>

                                                        <div className="he-itemInfo">
                                                            <div className="he-itemTop">
                                                                <div className="he-itemTitle">
                                                                    {isCommand ? `Comanda #${item.sourceId}` : "Entrada externa"}
                                                                </div>
                                                                <div className="he-itemValue">{formatBRL(item.amount)}</div>
                                                            </div>

                                                            <div className="he-itemSub">{item.description || "Sem descrição"}</div>

                                                            <div className="he-itemMeta">
                                                                <span className="he-metaPill">{formatDateTime(item.createdAt)}</span>
                                                                <span className={`he-badge ${isCommand ? "is-command" : "is-external"}`}>
                                                                    {isCommand ? "Comanda" : "Externa"}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="he-chevron" aria-hidden="true">
                                                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
                                                            <path
                                                                d="M10 6l6 6-6 6"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>

                                </div>
                            ))
                        )}

                        {!loadingList && rows.length > 0 ? (
                            <div className="he-pagination">
                                {hasMore ? (
                                    <button
                                        type="button"
                                        className="he-loadMore"
                                        onClick={fetchNextPage}
                                        disabled={loadingMore}
                                    >
                                        {loadingMore ? "Carregando..." : "Carregar mais"}
                                    </button>
                                ) : (
                                    <div className="he-end">Você chegou ao fim.</div>
                                )}
                            </div>
                        ) : null}
                    </section>
                </main>
            </div>
            <div style={{ height: "calc(50px + 72px)" }}></div>
        </div>
    );
}
